import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../../components/Card';
import { StatCard } from '../../../components/StatCard';
import { Tabs, TabsList, TabsTrigger } from '../../../components/Tabs';
import { useAttendanceStore } from '../../../store/attendanceStore';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CHART_TOOLTIP_ITEM } from '../../../lib/chartColors';
import { DayStatusBadge, fmtDate, fmtMinutes } from './attendanceUi';

/** The employee's own trends, computed only from recorded attendance. */
export function AttendanceReports() {
    const { reports, fetchReports, today } = useAttendanceStore();
    const [range, setRange] = useState<'week' | 'month'>('week');

    useEffect(() => { fetchReports(range).catch((e) => toast.error(e.message)); }, [range, fetchReports]);

    const stats = useMemo(() => {
        const worked = reports.filter((r) => r.hours > 0);
        const total = reports.reduce((n, r) => n + r.hours, 0);
        const office = reports.reduce((n, r) => n + r.officeHours, 0);
        const remote = reports.reduce((n, r) => n + r.wfhHours, 0);
        return {
            totalMinutes: Math.round(total * 60),
            avgMinutes: worked.length ? Math.round((total / worked.length) * 60) : 0,
            present: reports.filter((r) => r.status === 'PRESENT' || r.status === 'HALF_DAY').length,
            late: reports.filter((r) => r.isLate).length,
            missing: reports.filter((r) => r.hasMissingCheckout).length,
            remoteShare: office + remote > 0 ? Math.round((remote / (office + remote)) * 100) : null,
        };
    }, [reports]);
    const expectedHours = today?.day.expectedMinutes ? today.day.expectedMinutes / 60 : null;
    const data = reports.map((r) => ({ date: r.date, label: fmtDate(r.date, { day: 'numeric', month: 'short' }), hours: Math.round(r.hours * 100) / 100 }));

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                <Tabs value={range} onValueChange={(v) => setRange(v as 'week' | 'month')}>
                    <TabsList>
                        <TabsTrigger value="week">Last 7 days</TabsTrigger>
                        <TabsTrigger value="month">Last 30 days</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard label="Hours worked" value={fmtMinutes(stats.totalMinutes)} hint={`${fmtMinutes(stats.avgMinutes)} per day worked`} />
                <StatCard label="Days present" value={stats.present} hint={`${stats.late} late`} />
                <StatCard label="Needs attention" value={stats.missing} hint="Days with a missing check-out" />
            </div>

            <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                    <div>
                        <h2 className="font-display text-card-title text-foreground">Hours per day</h2>
                        <p className="text-xs text-muted-foreground">
                            Counted hours only — sessions without a check-out count once an adjustment is approved.
                            {stats.remoteShare !== null ? ` ${stats.remoteShare}% of hours worked remotely.` : ''}
                        </p>
                    </div>
                </div>
                {data.length === 0 ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground">No attendance recorded in this period.</p>
                ) : (
                    <div className="h-64 px-2 py-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                                <XAxis dataKey="label" stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} width={32} unit="h" />
                                <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} cursor={{ fill: 'hsl(var(--secondary))' }}
                                    formatter={(v: number) => [fmtMinutes(v * 60), 'Worked']} />
                                {expectedHours && <ReferenceLine y={expectedHours} stroke={CHART_AXIS} strokeDasharray="4 4"
                                    label={{ value: `Expected ${expectedHours}h`, position: 'insideTopRight', fill: CHART_AXIS, fontSize: 11 }} />}
                                <Bar dataKey="hours" fill={CHART.blue} radius={[4, 4, 0, 0]} maxBarSize={32} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            {reports.length > 0 && (
                <Card>
                    <div className="border-b border-border px-4 py-3"><h2 className="font-display text-card-title text-foreground">Day by day</h2></div>
                    <table className="w-full text-sm">
                        <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                            <tr><th className="px-4 py-2 font-semibold">Date</th><th className="px-4 py-2 font-semibold">Status</th><th className="px-4 py-2 text-right font-semibold">Worked</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {[...reports].reverse().map((r) => (
                                <tr key={r.date}>
                                    <td className="px-4 py-2 text-foreground">{fmtDate(r.date)}</td>
                                    <td className="px-4 py-2"><DayStatusBadge status={r.status} /></td>
                                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{fmtMinutes(r.hours * 60)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}
